import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProjectContext = createContext(null);

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/projects");
      setProjects(r.data);
      const prevId =
        (typeof window !== "undefined" &&
          window.localStorage.getItem("currentProjectId")) || null;
      const chosen =
        (prevId && r.data.find((p) => p.project_id === prevId)) || r.data[0] || null;
      setCurrent(chosen);
    } catch (err) {
      // Most common path here is a 401 because the Auth0 token expired between
      // pages. Don't surface as an unhandled rejection — clear state and let
      // AuthContext.checkAuth() drive the redirect to /login.
      setProjects([]);
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selectProject = (id) => {
    const p = projects.find((x) => x.project_id === id);
    if (p) {
      setCurrent(p);
      window.localStorage.setItem("currentProjectId", id);
    }
  };

  const createProject = async (payload) => {
    const r = await api.post("/projects", payload);
    setProjects((prev) => [...prev, r.data]);
    setCurrent(r.data);
    window.localStorage.setItem("currentProjectId", r.data.project_id);
    return r.data;
  };

  const updateCurrent = async (payload) => {
    if (!current) return;
    const r = await api.put(`/projects/${current.project_id}`, payload);
    setProjects((prev) =>
      prev.map((p) => (p.project_id === current.project_id ? r.data : p))
    );
    setCurrent(r.data);
  };

  return (
    <ProjectContext.Provider
      value={{ projects, current, selectProject, createProject, updateCurrent, loading, refresh }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => useContext(ProjectContext);
